import  { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Box,
  Avatar,
  Skeleton,
} from "@mui/material";
import {
  setSelectedCompanyId,
  setSelectedCompanyDetails,
  setKycData,
} from "../features/counter/kycSlice";
import { setCredentials } from "../features/counter/Authslice.js";
import config from "../config";

const KycManagement = () => {
  const { API_BASE_URL } = config;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, token } = useSelector((state) => state.auth);
  const { kycData } = useSelector((state) => state.kyc);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserAndKycData = async () => {
      try {
        setLoading(true);
        const userResponse = await axios.get(`${API_BASE_URL}/api/v1/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        dispatch(setCredentials({ token, user: userResponse.data.user }));

        const kycResponse = await axios.get(
          `${API_BASE_URL}/api/v1/getKycData/${userResponse.data.user.id}`
        );

        const kycDataArray = Array.isArray(kycResponse.data.kycData)
          ? kycResponse.data.kycData
          : [];

        dispatch(setKycData(kycDataArray));
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUserAndKycData();
    }
  }, [API_BASE_URL, token, dispatch]);

  const handleCompanySelect = (company) => {
    dispatch(setSelectedCompanyId(company._id));
    dispatch(
      setSelectedCompanyDetails({
        companyInfo: company.companyInfo,
        address: company.address,
        contactInfo: company.contactInfo,
        businessInfo: company.businessInfo,
        taxInfo: company.taxInfo,
        complianceInfo: company.complianceInfo,
        bankingInfo: company.bankingInfo,
      })
    );
    navigate("/Dashboard");
  };

  const handleAddNewKyc = () => {
    navigate("/kycForm");
  };

  const kycDataArray = Array.isArray(kycData) ? kycData : [];

  const memoizedTable = useMemo(() => {
    return (
      <TableBody>
        {kycDataArray.map((company) => (
          <TableRow key={company._id || company.companyInfo.registrationNumber}>
            <TableCell>{company.companyInfo.legalName}</TableCell>
            <TableCell>{company.companyInfo.registrationNumber}</TableCell>
            <TableCell>
              <Button
                sx={{ backgroundColor: "#EB5A3C", color: "black" }}
                variant="outlined"
                onClick={() => handleCompanySelect(company)}
              >
                Select
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  }, [kycDataArray]);

  return (
    <Box sx={{ padding: 2 }}>
    
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
          <Box>
            <Skeleton variant="text" width={120} />
            <Skeleton variant="text" width={180} />
          </Box>
        </Box>
      ) : user ? (
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Avatar
            sx={{ width: 56, height: 56, mr: 2, bgcolor: "primary.main" }}
            src={user.avatar}
          >
            {user.name ? user.name[0].toUpperCase() : "U"}
          </Avatar>
          <Box>
            <Typography variant="h6">{user.username}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography>Loading user data...</Typography>
      )}

    
      {loading ? (
        <Box>
          <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} />
        </Box>
      ) : kycDataArray.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            No KYC Information Found
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNewKyc}
          >
            Add KYC Information
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" gutterBottom>
            Select Your Company
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Registration Number</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              {memoizedTable}
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Button
              sx={{ backgroundColor: "#EB5A3C", color: "black" }}
              variant="contained"
              onClick={handleAddNewKyc}
            >
              Add New Company
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default KycManagement;
